import { z } from 'zod';
import { concatBytes, fromBase64Url, ownBytes, toBase64Url, utf8 } from './encoding';
import { LOGIN_KDF, loginKeyFrom, stretchPassword } from './login-key';
import { generateRecoveryCode, parseRecoveryCode } from './recovery';

/** HKDF labels for the two keys that wrap the vault key. */
export const VAULT_WRAP_INFO = {
  password: 'curentia/v1/vault-wrap/password',
  recovery: 'curentia/v1/vault-wrap/recovery',
} as const;

/** AES-256-GCM output: a 12-byte nonce and the ciphertext with its tag, both base64url. */
export const sealedBlobSchema = z.strictObject({
  iv: z.string().regex(/^[A-Za-z0-9_-]{16}$/),
  data: z.string().regex(/^[A-Za-z0-9_-]+$/),
});
export type SealedBlob = z.infer<typeof sealedBlobSchema>;

/**
 * What a device keeps unencrypted beside its vault: the login salt and the
 * vault key wrapped twice. Without the password or the recovery code it
 * reveals nothing.
 */
export const vaultKeyringSchema = z.strictObject({
  v: z.literal(1),
  kdf: z.literal(LOGIN_KDF.id),
  salt: z.string().regex(/^[A-Za-z0-9_-]{22}$/),
  password: sealedBlobSchema,
  recovery: sealedBlobSchema,
});
export type VaultKeyring = z.infer<typeof vaultKeyringSchema>;

/** The password or recovery code was wrong, or the keyring was damaged. */
export class WrongSecretError extends Error {
  constructor(message = 'The password or recovery code is not right') {
    super(message);
    this.name = 'WrongSecretError';
  }
}

async function gcmEncrypt(key: CryptoKey, plaintext: Uint8Array, aad: Uint8Array, iv?: Uint8Array): Promise<SealedBlob> {
  const nonce = ownBytes(iv ?? globalThis.crypto.getRandomValues(new Uint8Array(12)));
  const data = await globalThis.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: nonce, additionalData: ownBytes(aad), tagLength: 128 },
    key,
    ownBytes(plaintext),
  );
  return { iv: toBase64Url(nonce), data: toBase64Url(new Uint8Array(data)) };
}

async function gcmDecrypt(key: CryptoKey, blob: SealedBlob, aad: Uint8Array): Promise<Uint8Array<ArrayBuffer>> {
  const data = await globalThis.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: fromBase64Url(blob.iv), additionalData: ownBytes(aad), tagLength: 128 },
    key,
    fromBase64Url(blob.data),
  );
  return new Uint8Array(data);
}

async function hkdfAesKey(base: CryptoKey, info: string): Promise<CryptoKey> {
  return globalThis.crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(0), info: utf8(info) },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

const wrapAad = (kind: keyof typeof VAULT_WRAP_INFO) => utf8(`curentia/v1/vault-key\0${kind}`);

/**
 * The random 256-bit key that encrypts everything a device stores. It exists
 * only in memory while the app is unlocked. Each stored item is bound to its
 * own context string, so items cannot be swapped between places.
 */
export class VaultKey {
  private raw: Uint8Array<ArrayBuffer> | null;
  private readonly key: CryptoKey;

  private constructor(raw: Uint8Array<ArrayBuffer>, key: CryptoKey) {
    this.raw = raw;
    this.key = key;
  }

  static async fromBytes(bytes: Uint8Array): Promise<VaultKey> {
    if (bytes.length !== 32) throw new Error('A vault key has 32 bytes');
    const raw = ownBytes(bytes);
    const key = await globalThis.crypto.subtle.importKey('raw', raw, 'AES-GCM', false, ['encrypt', 'decrypt']);
    return new VaultKey(raw, key);
  }

  static generate(): Promise<VaultKey> {
    return VaultKey.fromBytes(globalThis.crypto.getRandomValues(new Uint8Array(32)));
  }

  private bytes(): Uint8Array<ArrayBuffer> {
    if (!this.raw) throw new Error('The vault is locked');
    return this.raw;
  }

  /** Encrypts bytes for storage under a context such as "record:abc". */
  async encrypt(plaintext: Uint8Array, context: string): Promise<SealedBlob> {
    this.bytes();
    return gcmEncrypt(this.key, plaintext, utf8(`curentia/v1/vault\0${context}`));
  }

  /** Decrypts what encrypt stored under the same context. Throws if anything changed. */
  async decrypt(blob: SealedBlob, context: string): Promise<Uint8Array<ArrayBuffer>> {
    this.bytes();
    return gcmDecrypt(this.key, blob, utf8(`curentia/v1/vault\0${context}`));
  }

  async encryptJson(value: unknown, context: string): Promise<SealedBlob> {
    return this.encrypt(utf8(JSON.stringify(value)), context);
  }

  async decryptJson<T = unknown>(blob: SealedBlob, context: string): Promise<T> {
    return JSON.parse(new TextDecoder().decode(await this.decrypt(blob, context))) as T;
  }

  /** Wraps this key under a wrapping key. Only the keyring functions use this. */
  async wrapUnder(wrapKey: CryptoKey, kind: keyof typeof VAULT_WRAP_INFO, iv?: Uint8Array): Promise<SealedBlob> {
    return gcmEncrypt(wrapKey, this.bytes(), wrapAad(kind), iv);
  }

  /** Forgets the key. Any later use throws. JavaScript cannot promise the bytes leave memory at once. */
  destroy(): void {
    this.raw?.fill(0);
    this.raw = null;
  }

  get destroyed(): boolean {
    return this.raw === null;
  }
}

/**
 * Stretches the password once and derives both the login key for the server
 * and the key that wraps the vault key. The server only ever sees the login key.
 */
export async function passwordSecrets(password: string, salt: string): Promise<{ loginKey: string; wrapKey: CryptoKey }> {
  const base = await stretchPassword(password, fromBase64Url(salt));
  const [loginKey, wrapKey] = await Promise.all([loginKeyFrom(base), hkdfAesKey(base, VAULT_WRAP_INFO.password)]);
  return { loginKey, wrapKey };
}

/** The key a recovery code wraps the vault key with. Throws WrongSecretError for text that is not a code. */
export async function recoveryWrapKey(code: string): Promise<CryptoKey> {
  const bytes = parseRecoveryCode(code);
  if (!bytes) throw new WrongSecretError('That is not a recovery code');
  const base = await globalThis.crypto.subtle.importKey('raw', bytes, 'HKDF', false, ['deriveKey']);
  return hkdfAesKey(base, VAULT_WRAP_INFO.recovery);
}

async function unwrap(wrapKey: CryptoKey, blob: SealedBlob, kind: keyof typeof VAULT_WRAP_INFO): Promise<VaultKey> {
  let raw: Uint8Array<ArrayBuffer>;
  try {
    raw = await gcmDecrypt(wrapKey, blob, wrapAad(kind));
  } catch {
    throw new WrongSecretError();
  }
  return VaultKey.fromBytes(raw);
}

/**
 * Sets up a new vault: a random vault key wrapped by the password and by a
 * new recovery code. Show the recovery code once and never store it.
 */
export async function createVault(password: string, salt: string) {
  const vaultKey = await VaultKey.generate();
  const { loginKey, wrapKey } = await passwordSecrets(password, salt);
  const recoveryCode = generateRecoveryCode();
  const keyring: VaultKeyring = {
    v: 1,
    kdf: LOGIN_KDF.id,
    salt,
    password: await vaultKey.wrapUnder(wrapKey, 'password'),
    recovery: await vaultKey.wrapUnder(await recoveryWrapKey(recoveryCode), 'recovery'),
  };
  return { vaultKey, keyring, recoveryCode, loginKey };
}

/** Opens the vault with the password. Also returns the login key, so one stretch serves both. */
export async function unlockWithPassword(keyring: VaultKeyring, password: string) {
  const { loginKey, wrapKey } = await passwordSecrets(password, keyring.salt);
  return { vaultKey: await unwrap(wrapKey, keyring.password, 'password'), loginKey };
}

/** Opens the vault with the recovery code. */
export async function unlockWithRecoveryCode(keyring: VaultKeyring, code: string): Promise<VaultKey> {
  return unwrap(await recoveryWrapKey(code), keyring.recovery, 'recovery');
}

/**
 * Re-wraps the vault key under a new password, for a password change or a
 * reset after recovery. The salt stays the same, so the new login key is
 * returned for the server.
 */
export async function setVaultPassword(keyring: VaultKeyring, vaultKey: VaultKey, newPassword: string) {
  const { loginKey, wrapKey } = await passwordSecrets(newPassword, keyring.salt);
  return { keyring: { ...keyring, password: await vaultKey.wrapUnder(wrapKey, 'password') }, loginKey };
}

/** Replaces the recovery code. The old code stops working for this keyring. */
export async function replaceRecoveryCode(keyring: VaultKeyring, vaultKey: VaultKey) {
  const recoveryCode = generateRecoveryCode();
  const recovery = await vaultKey.wrapUnder(await recoveryWrapKey(recoveryCode), 'recovery');
  return { keyring: { ...keyring, recovery }, recoveryCode };
}

/** For fixed test vectors only: the wrapping with a chosen nonce. */
export const testing = {
  async wrapWithPassword(vaultKey: VaultKey, password: string, salt: string, iv: Uint8Array) {
    const { wrapKey } = await passwordSecrets(password, salt);
    return vaultKey.wrapUnder(wrapKey, 'password', iv);
  },
  async wrapWithRecoveryCode(vaultKey: VaultKey, code: string, iv: Uint8Array) {
    return vaultKey.wrapUnder(await recoveryWrapKey(code), 'recovery', iv);
  },
  aad: (kind: keyof typeof VAULT_WRAP_INFO) => concatBytes(wrapAad(kind)),
};
